import { createContext, ReactNode, useState } from "react";
import { autoUpdate, size, useClick, useDismiss, useFloating, useInteractions } from "@floating-ui/react";
import { classNames } from "@ct-react/core";
import { useMediaQueryDown } from "../../../hooks/media-queries";
import "./stickable-box.scss";

type State = { sticked: boolean };

type MainProps = {
  className?: string;
  resume: ReactNode;
  content: ReactNode;
};

export const StickableContext = createContext<State>({ sticked: false });

const StickableBox = ({ className, resume, content }: MainProps) => {

  const [ mobileOpen, setMobileOpen ] = useState<boolean>(false);

  const { context, refs, floatingStyles } = useFloating({
    open: mobileOpen,
    onOpenChange: setMobileOpen,
    strategy: "fixed",
    placement: "top-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      size({
        apply: ({ elements, availableHeight }) => {
          elements.floating.style.setProperty("--ccv-stick-inner-max-height", `${availableHeight}px`);
        }
      })
    ]
  });

  const isDisplayUntilTablet = useMediaQueryDown("tablet");
const isDisplayMobileLandscape = useMediaQueryDown("desktop", "landscape");
const sticked = isDisplayUntilTablet || isDisplayMobileLandscape;

const { getReferenceProps, getFloatingProps } = useInteractions([
  useClick(context, {
    enabled: sticked,
    toggle: true,
  }),
  useDismiss(context, {
    enabled: sticked,
    outsidePress: true,
    ancestorScroll: true,
  }),
]);

  const wrapperClasses = classNames("stickable-box", className, {
    "sticky-resume": sticked,
    "direct-content": !sticked
  });

  return (
    <StickableContext value={{ sticked }}>
      <div ref={refs.setReference}
           {...getReferenceProps()}
           className={wrapperClasses}>
        {sticked ? resume : content}
      </div>
      {(sticked && mobileOpen) &&
        <div className="stickable-content"
             ref={refs.setFloating}
             {...getFloatingProps()}
             style={floatingStyles}>
          {content}
        </div>
      }
    </StickableContext>);

}

export default StickableBox;
